/**
 * Chapters Page
 * Displays chapters within a novel with sortable list
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import type { Novel } from "@novelreader/types";
import type { ChapterListItem } from "../lib/api";
import { api } from "../lib/api";
import { ChapterRow } from "../components/ChapterRow";

// ---------------------------------------------------------------------------
// Loading Spinner Component
// ---------------------------------------------------------------------------

function LoadingSpinner() {
  return (
    <div
      data-testid="loading-spinner"
      className="flex justify-center items-center py-12"
    >
      <svg
        className="animate-spin h-8 w-8 text-indigo-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State Component
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  novelId: string;
}

function EmptyState({ novelId }: EmptyStateProps) {
  return (
    <div className="text-center py-12">
      <svg
        className="mx-auto h-12 w-12 text-slate-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3 className="mt-4 text-lg font-medium text-slate-900">
        No chapters yet
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        Get started by adding your first chapter to this novel.
      </p>
      <Link
        to={`/novels/${novelId}/chapters/new`}
        className="mt-6 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
      >
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Add your first chapter
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error State Component
// ---------------------------------------------------------------------------

interface ErrorStateProps {
  onRetry: () => void;
}

function ErrorState({ onRetry }: ErrorStateProps) {
  return (
    <div className="text-center py-12">
      <svg
        className="mx-auto h-12 w-12 text-red-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h3 className="mt-4 text-lg font-medium text-slate-900">
        Failed to load chapters
      </h3>
      <p className="mt-2 text-sm text-slate-600">
        There was an error loading the chapters. Please try again.
      </p>
      <button
        onClick={onRetry}
        className="mt-6 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
      >
        Retry
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function Chapters() {
  const { id: novelId } = useParams<{ id: string }>();

  const [novel, setNovel] = useState<Novel | null>(null);
  const [chapters, setChapters] = useState<ChapterListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!novelId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [novelResponse, chaptersResponse] = await Promise.all([
        api.novels.get(novelId),
        api.chapters.list(novelId),
      ]);

      setNovel(novelResponse.novel);
      setChapters(chaptersResponse.chapters);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [novelId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (chapterId: string) => {
    if (!novelId) return;

    try {
      await api.chapters.delete(novelId, chapterId);
      // Refetch chapters after deletion
      const chaptersResponse = await api.chapters.list(novelId);
      setChapters(chaptersResponse.chapters);
    } catch (err) {
      // Could show an error toast here
      console.error("Failed to delete chapter:", err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-9 w-24 bg-slate-200 rounded animate-pulse" />
            <div className="h-9 w-28 bg-slate-200 rounded animate-pulse" />
          </div>
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Chapters</h1>
        <ErrorState onRetry={fetchData} />
      </div>
    );
  }

  // Novel not found
  if (!novel || !novelId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Chapters</h1>
        <div className="text-slate-600">Novel not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/novels" className="hover:text-slate-700">
          Novels
        </Link>
        <span>/</span>
        <span className="text-slate-900 font-medium">{novel.title}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{novel.title}</h1>
        <div className="flex items-center gap-2">
          <Link
            to={`/novels/${novelId}/edit`}
            className="inline-flex items-center px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit Novel
          </Link>
          <Link
            to={`/novels/${novelId}/chapters/new`}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Chapter
          </Link>
        </div>
      </div>

      {/* Content */}
      {chapters.length === 0 ? (
        <EmptyState novelId={novelId} />
      ) : (
        <div
          data-testid="chapters-list"
          className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
        >
          {chapters.map((chapter) => (
            <ChapterRow
              key={chapter.id}
              chapter={chapter}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
